import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import SharedContentQuery from './__generated__/SharedContentQuery.graphql.ts';
import type {
	SharedContentQuery as SharedContentQueryType,
	SharedContentQueryVariables,
} from './__types__/SharedContentQuery.ts';

export type AccessLinkContent = {
	contentId: string;
	spaceKey: string;
	type: string | null;
	subType: string | null;
};

/**
 * Resolves a share token into the content identifiers the real view page needs. The access-link
 * URL carries only the token, so unlike the normal view-page route a preloader cannot read
 * `contentId`/`spaceKey` off `match.params` and has to ask the backend.
 *
 * Returns `undefined` for a token that resolves to nothing usable — invalid, expired or turned
 * off — so the caller can fall back to the simplified path.
 */
export const resolveAccessLinkContent = async (
	shareToken: string,
): Promise<AccessLinkContent | undefined> => {
	const result = await query<SharedContentQueryType, SharedContentQueryVariables>({
		query: SharedContentQuery,
		variables: {
			shareToken,
		},
		context: {
			// Without this the GraphQL middleware silently blocks the request.
			allowOnExternalPage: true,
		},
	});

	const singleContent = result?.data?.singleContent;
	const contentId = singleContent?.id;
	const space = singleContent?.space;
	const spaceKey = space?.alias || space?.key;

	if (!contentId || !spaceKey) {
		return undefined;
	}

	return {
		contentId,
		spaceKey,
		type: singleContent?.type ?? null,
		subType: singleContent?.subType ?? null,
	};
};
